import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { saveAs } from 'file-saver';
import { getDocument, getPdfBytes, setDocumentViewed, updateFavouriteFlagAsync } from "shared/services/railDocumentsApi";
import { RailDocument, RailDocumentPendingActions } from 'shared/models/api/models';
import SubHeader from "components/menu/SubHeader";
import { RelatedDocuments } from './RelatedDocuments'
import { AcknowledgePanel } from './AcknowledgeDocument'
import { DocumentTypeEnum } from 'shared/enums/document-type-enum';
import SideBar from './SideBar'
import './Viewer.css'
import { useProfile } from '../../../hooks/useRoles';
import { ViewInfo } from "components/portal/documents/DocumentInfo/DocumentInfo";
import { findDocumentViewForId } from '../../ui/DocumentRules';
import { applicationState, getUserExistingRulebookMode } from '../../../shared/store/applicationState';
import { GridRulebookLabel } from '../GridComponents';
import { CleverString } from '../../ui/CleverString';
import PDFViewer from './PDFViewer';

export const Viewer = () => {
    const params = useParams();
    const user = useProfile();

    const [relatedDocuments, setRelatedDocuments] = useState([] as Array<RailDocument>);
    const [document, setDocument] = useState({} as RailDocument);
    const [showDocumentInfo, setShowDocumentInfo] = useState(0);
    const [documentLoadError, setDocumentLoadError] = useState(null as string | null);

    const [pdfInstance, setPdfInstance] = useState<any>(null);

    const navigate = useNavigate();
    const location = useLocation();

    // Derived directly from the route param rather than held in state — it only changes when
    // the route changes, which re-renders the component anyway.
    const documentId = Number.parseInt(params.documentId ?? "");

    const toggleFavourite = async () => {
        await updateFavouriteFlagAsync(documentId, !document.isFavourite);
        setDocument(prev => ({ ...prev, isFavourite: !prev.isFavourite }));
    };

    const shareDocument = () => {
        const subject = "BHP Rail Portal: " + user?.firstname + " wants to share a document.";
        const body = "Please click this link: " + window.location + " to view the " + document.title + ".";

        const link = "mailto:?subject=" + encodeURIComponent(subject) + "&body=" + encodeURIComponent(body);

        window.open(link, "_blank");
    };

    const downloadPdf = async () => {
        // Re-fetch fresh bytes rather than reusing `pdfInstance`: pdf.js/react-pdf transfers that ArrayBuffer to its worker
        // (a zero-copy transfer), which detaches it in this thread — after the viewer loads, `pdfInstance.byteLength` is 0, so wrapping it
        // in a Blob here would produce an empty file. See https://github.com/wojtekmaj/react-pdf/issues/1657
        const pdf = await getPdfBytes(documentId.toString());

        const blob = new Blob([pdf], { type: "application/pdf" });
        saveAs(blob, document.fileName);
    }

    const printPdf = async () => {
        // Re-fetch fresh bytes (same buffer-detachment reason as downloadPdf) and print the PDF itself
        // via a hidden iframe, rather than window.print() which would print the whole page.
        // Note: `document` here is component state (a RailDocument), so the DOM is reached via `window.document`.
        const pdf = await getPdfBytes(documentId.toString());
        const url = URL.createObjectURL(new Blob([pdf], { type: "application/pdf" }));

        const iframe = window.document.createElement("iframe");
        iframe.style.display = "none";
        iframe.src = url;
        iframe.onload = () => {
            const frameWindow = iframe.contentWindow;
            if (!frameWindow) {
                return;
            }
            frameWindow.onafterprint = () => {
                URL.revokeObjectURL(url);
                iframe.remove();
            };
            frameWindow.focus();
            frameWindow.print();
        };
        window.document.body.appendChild(iframe);
    }

    const [documentPendingActions, setDocumentPendingActions] = useState({} as RailDocumentPendingActions);
    const [displayAcknowledgePanel, setDisplayAcknowledgePanel] = useState(false);

    useEffect(() => {
        if (params.documentId === undefined) {
            return;
        }

        let mounted = true;

        const mount = async () => {
            const rulebookMode = getUserExistingRulebookMode();
            const doc = await getDocument(documentId, rulebookMode!);

            // Access control: block CBTC documents (or CBTC-only documents opened via the traditional route) for users without the CBTC
            // edit permission. Done here — right after the document loads and before fetching related documents or PDF bytes — so
            // unauthorised users are redirected immediately without loading anything further.
            const cbtcOnlyDocument = !doc?.traditional && location.pathname.includes('traditional');
            if (location.pathname.includes('cbtc') || cbtcOnlyDocument) {
                const profile = applicationState.getProfile();
                if (!profile?.roles.some(role => role.permissions.includes('web.cbtc.user.edit')) || cbtcOnlyDocument) {
                    navigate('/NoAccess');
                    return;
                }
            }

            const related = await Promise.all((doc.relatesTo || []).map(async (x) => await getDocument(x.key, rulebookMode!)));

            if (mounted) {
                setDocument(doc);
                setRelatedDocuments(related);
            }

            let pdf = undefined as undefined | ArrayBuffer;
            if (!!doc.id) {
                pdf = await getPdfBytes(doc.id.toString());
            }

            if (mounted) {
                if (pdf) {
                    setPdfInstance(pdf);
                    setDocumentViewed(doc.id).then(pending => {
                        // A documentId of 0 is the server's sentinel for "no pending actions" —
                        // ignore it so the acknowledge panel only shows for documents that
                        // actually have a pending action.
                        if (mounted && pending.documentId !== 0) {
                            setDocumentPendingActions(pending);
                        }
                    });
                }
                else {
                    setDocumentLoadError("You do not have access/permission to view this document. Please contact your supervisor.");
                }
            }
        }

        mount();

        return () => {
            mounted = false;
            setDisplayAcknowledgePanel(false);
        };

    }, [params.documentId]);

    const clearModals = () => {
        setShowDocumentInfo(0);
    };

    const onDocInfoUpdate = async () => {
        const rulebookMode = getUserExistingRulebookMode();
        const doc = await getDocument(documentId, rulebookMode!);
        const related = await Promise.all((doc.relatesTo || []).map(async (x) => await getDocument(x.key, rulebookMode!)));
        setDocument(doc);
        setRelatedDocuments(related);
    }

    const customBreadCrumbs = () => {
        const moduleLinkPart = (document.moduleId === 1) ? 'rulebook' : 'raildocs';
        const typeLinkPart = DocumentTypeEnum[document.typeId];
        const subTypeLinkPart = DocumentTypeEnum[document.subTypeId];

        const viewData = findDocumentViewForId(document.typeId, document.subTypeId);

        const homeLink = `/`;
        const moduleLink = viewData?.module.url ?? `/${moduleLinkPart}`;
        const typeLink = viewData?.type.url ?? `/${moduleLinkPart}/${typeLinkPart}`;
        const subTypeLink = viewData?.subType?.url ?? `/${moduleLinkPart}/${subTypeLinkPart}`;

        return (
            <>
                <span className="breadcumb">
                    <Link to={homeLink} className="breadcrumb-section">Home</Link>
                    {!documentLoadError &&
                        <>
                        &nbsp;&gt; <Link to={moduleLink} className="breadcrumb-section">{new CleverString(moduleLinkPart).toTitleCase()}</Link>
                            &nbsp;&gt; <Link to={typeLink} className="breadcrumb-section">{document.type}</Link>
                            {subTypeLinkPart !== undefined &&
                                <>&nbsp;&gt; <Link to={subTypeLink} className="breadcrumb-section">{document.subType}</Link></>
                            }
                        &nbsp;&gt; <span className="breadcrumb-section">{document.title}</span>
                        <GridRulebookLabel cbtc={document.cbtc } traditional={ document.traditional } /> 
                        </>
                    }
                </span>
            </>
        )
    }

    return (
        <div className="document-list">
            <div className="pdf-container-outer narrow-container">
                {documentId > 0 ? <SubHeader customBreadCrumbs={customBreadCrumbs} /> : <SubHeader />}

                <div className="pdf-nav-body">
                    {documentLoadError ?
                        <div>{documentLoadError}</div>
                        :
                        <>
                            <div className="pdf-container">
                                { pdfInstance &&
                                    <PDFViewer
                                      file={pdfInstance}
                                      isFavourite={document?.isFavourite}
                                      onLastPageReached={setDisplayAcknowledgePanel}
                                      onDownload={downloadPdf}
                                      onPrint={printPdf}
                                      onShare={shareDocument}
                                      onFavourite={toggleFavourite}/>
                                }
                            </div>
                            <AcknowledgePanel documentId={documentId} pendingActions={documentPendingActions} displayAcknowledgePanel={displayAcknowledgePanel} />
                            <RelatedDocuments document={document} relatedDocuments={relatedDocuments} />
                        </>
                    }
                </div>

                <ViewInfo isOpen={showDocumentInfo > 0} onClose={clearModals} onUpdate={onDocInfoUpdate} documentId={showDocumentInfo} />

                {params.documentId !== undefined &&
                    <SideBar docId={params.documentId} typeId={document.typeId} subTypeId={document.subTypeId} />
                }
            </div>
        </div>
    );
}
